import { animate, state, style, transition, trigger } from '@angular/animations';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, combineLatest, timer } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { DividendVipService } from 'src/app/pages/dividend-vip/dividend-vip.service';
import { FriendService } from 'src/app/pages/friend/friend.service';
import { ReferralIdComponent } from 'src/app/pages/friend/recommend/dialog/referral-id/referral-id.component';
import { MiniGameService } from 'src/app/pages/minigame/minigame.service';
import { SettingsService } from 'src/app/pages/settings/settings.service';
import { AllianceApi } from 'src/app/shared/apis/alliance.api';
import { LangModel } from 'src/app/shared/interfaces/country.interface';
import { CurrenciesInterface } from 'src/app/shared/interfaces/deposit.interface';
import { LabelScenesRedirectMethodEnum } from 'src/app/shared/interfaces/game.interface';
import { ChatService } from 'src/app/shared/service/chat.service';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { PopupService } from 'src/app/shared/service/popup.service';
import { ConversionCurrency, CurrencyWalletService } from '../currency-wallet/currency-wallet.service';
import { LeftMenuService } from './left-menu.service';

@UntilDestroy()
@Component({
  selector: 'app-left-menu',
  templateUrl: './left-menu.component.html',
  styleUrls: ['./left-menu.component.scss'],
  animations: [
    trigger('show', [
      state(
        'true',
        style({ transform: 'translate3d(0,0,0)', opacity: 1, visibility: 'visible', 'pointer-events': 'all' }),
      ),
      state(
        'false',
        style({ transform: 'translate3d(-100%, 0, 0)', opacity: 0, visibility: 'hidden', 'pointer-events': 'none' }),
      ),
      transition('false <=> true', animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
    ]),
    trigger('open', [
      state('true', style({ maxHeight: '90rem', overflow: 'hidden' })),
      state('false', style({ maxHeight: '0rem', overflow: 'hidden' })),
      transition('true <=> false', animate('300ms')),
    ]),
  ],
})
export class LeftMenuComponent implements OnInit, OnDestroy {
  domain = window.location.origin;

  logined!: boolean; //是否已登录

  isH5!: boolean; //是否h5

  menuData: any = this.leftMenuService.DEFAULT_MENU; // 基本菜单数据

  /** rail tool tips 位置 */
  railPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 14,
    },
  ];

  /** rail 的时候 切换主题颜色 的位置 */
  themePositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
    },
  ];

  state!: boolean;

  invisible?: boolean;

  transition!: 'start' | 'end';

  closed!: boolean;

  opened!: boolean;

  menuReferenceSystems: any = this.findMenuItem(this.leftMenuService.DEFAULT_MENU.systems, [
    'langSelect',
    'conversionCurrency',
    'themeSelect',
  ]);

  /** 订阅悬浮 */
  onShowTheme$: Subject<boolean> = new Subject();

  /** 当前激活的下标 */
  activePanelIndex: number = -1;

  /** 所哟语言 */
  allLangData!: LangModel[];

  /** 法定货币 */
  faitCurrencies!: CurrenciesInterface[];

  /** 展开后 下拉的 内容的定位 */
  fullDropItemPosition: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];

  /** 当前语言 */
  currentLanugage?: LangModel;

  /** popup 位置 */
  popupPosition!: ConnectedPosition[];

  /** 显示绑定推荐好友ID入口 */
  showBindReferralId!: boolean;

  /** 是否显示好友推荐链接弹窗 */
  isShowInviteCodeModel!: boolean;

  /** 最新優惠數量 */
  activityCount: number = 0;

  constructor(
    public currencyWalletService: CurrencyWalletService,
    public leftMenuService: LeftMenuService,
    private layout: LayoutService,
    public appService: AppService,
    private miniGameService: MiniGameService,
    public router: Router,
    public settingsService: SettingsService,
    private popup: PopupService,
    private allianceApi: AllianceApi,
    private friendService: FriendService,
    private route: ActivatedRoute,
    public localStorageService: LocalStorageService,
    public chatService: ChatService,
    private dividendVipService: DividendVipService,
  ) {
    //菜单开关相关
    combineLatest([
      this.layout.leftMenuState$, //伸展或收缩
      this.layout.leftMenuTransition$, //动画状态
      this.layout.leftMenuInvisible$.pipe(
        tap(x => {
          if (x) this.closeTooltip(this.menuData.list);
        }),
      ), //是否不可见
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([state, transition, invisible]) => {
        this.state = state;
        this.invisible = invisible;
        this.opened = state && transition === 'end';
        this.closed = !state && transition === 'end';
      });
  }

  /** 获取当前路由 */
  get getUrl(): string {
    return this.router.url.split('/').slice(2).join('/');
  }

  isRouteActive(page?: string | null): boolean {
    return typeof page === 'string' && !!page && this.getUrl === page;
  }

  /** 交换货币 */
  conversionCurrency?: ConversionCurrency;

  /** 左侧菜单准备 */
  isLeftMenuReady: boolean = false;

  ngOnInit() {
    this.logined = Boolean(this.localStorageService.loginToken);

    this.leftMenuService
      .onGetTags()
      .pipe(
        untilDestroyed(this),
        map(
          x =>
            x?.leftMenus?.map(item => {
              return {
                ident: item?.labelId || '',
                name: item?.name || '',
                showOnRail: true,
                railPage: this.leftMenuService.getLinkByMethod(item),
                railIcon: item?.menuIcon || '',
                railColorIcon: item?.icon || '',
                expand: false,
                canShowDropDown: item.redirectMethod === LabelScenesRedirectMethodEnum[2],
                children: [
                  ...(item.infoExpandList?.map(expendList => ({
                    name: expendList?.labelName || '',
                    ident: expendList?.labelCode || '',
                    page: this.leftMenuService.getLinkByMethod(expendList),
                    menuIcon: expendList?.menuIcon || '',
                    icon: expendList?.icon || '',
                    closeOnclick: true,
                  })) || []),
                ],
              };
            }) || [],
        ),
      )
      .subscribe(data => {
        this.leftMenuService.DEFAULT_MENU.list = data || [];
        if (data.length) {
          this.isLeftMenuReady = true;
          this.checkRouteToExpandMenu();
        }
      });

    this.layout.page$.pipe(untilDestroyed(this)).subscribe(() => this.checkRouteToExpandMenu());

    combineLatest([
      this.currencyWalletService.conversionCurrency$,
      this.appService.currencies$.pipe(map(x => x.filter(v => v.isVisible && !v.isDigital))),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([conversionCurrency, currencies]) => {
        this.faitCurrencies = currencies;
        const storageCc = currencies.find(
          currency => currency.currency === this.localStorageService.conversionCurrency,
        );
        this.conversionCurrency = {
          ...conversionCurrency,
          currency: storageCc as CurrenciesInterface,
        };

        this.menuReferenceSystems['conversionCurrency'].name = storageCc?.currency || 'None';

        this.menuReferenceSystems['conversionCurrency'].showOnRail = conversionCurrency?.enable || false;
      });

    // 需要登录状态改变执行的函数请放在这里
    if (this.logined) {
      // 初始获取小游戏收藏
      this.miniGameService.getFavoriteList().subscribe();
      // 初始获取小游戏最近玩过
      this.miniGameService.getRecentLyPlayedGame().subscribe();

      // 获取是否显示输入推荐ID
      this.isShowEnterReferralId();
      // 获取点击邀请好友跳转的页面
      this.getReferralLinkRoute();
      this.friendService.isShowLeftMenuReferraslId$.pipe(untilDestroyed(this)).subscribe(t => {
        this.showBindReferralId = t;
      });

      // 設定最新優惠數量
      this.dividendVipService.activityCount$.pipe(untilDestroyed(this)).subscribe(num => {
        this.activityCount = num;
      });
      this.dividendVipService.getActivityCount();
    } else {
      // 未登录时， 顶部钱包未显示时
      if (this.localStorageService.conversionCurrency) {
        this.currencyWalletService.initConversion();
      }
    }

    //订阅是否是h5
    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));

    //获取所有语言
    this.appService.languages$.pipe(untilDestroyed(this)).subscribe(x => {
      this.allLangData = x;
      const currentLang = x.filter(v => v.code === this.appService.languageCode)[0];
      this.menuReferenceSystems['langSelect'].name = currentLang?.code?.slice(0, 2)?.toUpperCase();
      this.currentLanugage = currentLang;
    });

    // this.appService.themeSwitch$.pipe(untilDestroyed(this)).subscribe(x => {
    //   if (x === 'sun') {
    //     this.menuReferenceSystems['themeSelect'].icon = `icon-${x}-rail`;
    //   } else {
    //     this.menuReferenceSystems['themeSelect'].icon = `icon-${x}`;
    //   }
    // });
  }

  ngOnDestroy(): void {
    this.leftMenuService.popupStatus = false;
  }

  /**
   * 鼠标移入悬浮
   *
   * @param item
   */
  onShowSubItem(item: any) {
    if (!item) return;
    this.onCloseSubItem(0, item);
    this.onShowTheme$.next(true);
    item.tooltip = true;
  }

  /**
   * 鼠标移除悬浮
   *
   * @param time
   * @param item
   */
  onCloseSubItem(time: number = 0, item: any) {
    if (!item) return;
    if (time > 0) {
      timer(time)
        .pipe(takeUntil(this.onShowTheme$))
        .subscribe(_ => {
          item.tooltip = false;
        });
    } else {
      item.tooltip = false;
    }
  }

  /**
   * 鼠标移入
   *
   * @param item
   */
  onMouseEnter(item: any) {
    item.tooltip = true;
  }

  /**
   * 鼠标移出
   *
   * @param item
   */
  onMouseLeave(item: any) {
    item.tooltip = false;
  }

  /**
   * 展示语言或者货币pannel
   *
   * @param index 下标
   */
  onSetSystem(index: number) {
    if (this.leftMenuService.popupStatus) {
      this.leftMenuService.popupStatus = false;
      return;
    }
    if (this.state) {
      if (!this.isH5) {
        this.popupPosition = [
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -14,
          },
        ];
      } else {
        this.popupPosition = [
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -20,
            offsetX: -6,
          },
        ];
      }
    } else {
      this.popupPosition = [
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetX: 14,
        },
      ];
    }
    this.activePanelIndex = index;
    this.leftMenuService.popupStatus = true;
  }

  /**
   * 选择某个用于显示的法币
   *
   * @param item
   */
  selectShowOnFiat(item: CurrenciesInterface | null) {
    this.leftMenuService.popupStatus = false;
    if (item) {
      this.currencyWalletService.updateConversion(true, item.currency);
    } else {
      this.currencyWalletService.updateConversion(false);
    }
  }

  /** 设置默认货币 */

  //按ident寻找某一个菜单
  findMenuItem(data: any[], idents: string[]): any {
    const result: any = {};
    data.forEach(x => {
      if (idents.includes(x.ident)) {
        result[x.ident] = x;
      }
      if (x.children) {
        x.children.forEach((v: any) => {
          if (idents.includes(v.ident)) {
            result[v.ident] = v;
          }
        });
      } else if (x.options) {
        x.options.forEach((v: any) => {
          if (idents.includes(v.ident)) {
            result[v.ident] = v;
          }
        });
      }
    });
    return result;
  }

  //关闭菜单
  turn(v: boolean) {
    this.leftMenuService.popupStatus = false;
    this.layout.leftMenuState$.next(v);
  }

  //获取跳转路由
  getRouterLink(page: any): string | null {
    return page ? this.getLink(page) : null;
  }

  //拼接跳转路由
  getLink(page: string): string {
    return `/${this.appService.languageCode}/` + page;
  }

  //获取激活的样式
  getRouterLinkActive(page: any): string {
    return page ? 'active' : '';
  }

  //点击任何菜单项。主要是展开/收起的功能。路由跳转请在left-menu.config文件中设置page。
  clickMenu(target: any, expandClick: boolean = false) {
    if (!this.isLeftMenuReady || this.checkDisabled(target)) return;

    this.leftMenuService.popupStatus = false;

    if (target?.needLogined && !this.logined) return;

    if (target.ident === 'onLines') {
      this.appService.toOnLineService$.next(true);
      return;
    }

    if (target.ident === 'chat') {
      this.chatService.toggleChat(this.isH5);
      this.turn(false);
      return;
    }

    // vip club popup open 还未上线
    if (target.ident === 'vip-club') {
      this.router.navigate([], { relativeTo: this.route, fragment: 'vip' });
      return;
    }

    if (typeof target?.canShowDropDown === 'boolean' && !target.canShowDropDown && expandClick) {
      const url = this.getRouterLink(target.railPage);
      if (url) this.router.navigateByUrl(url);
      if (this.isH5) {
        this.turn(false);
      }
      return;
    }

    if (target.children?.length) {
      this.toggleDropdownItem(target);
      return;
    }

    const url = this.getRouterLink(target.page);
    if (url) this.router.navigateByUrl(url);

    //存在下拉展开菜单，交由下拉展开操作
    if (target.options) return;

    // 当点击需要关闭菜单
    if (target.closeOnclick && this.isH5) {
      this.turn(false);
      return;
    }
  }

  private toggleDropdownItem(target: any) {
    const nextExpand = !target.expand;
    this.collapseDropdownGroups();
    target.expand = nextExpand;
  }

  private collapseDropdownGroups() {
    this.menuData?.list?.forEach((item: any) => {
      if (item?.children?.length) item.expand = false;
    });
  }

  private checkRouteToExpandMenu() {
    if (!this.menuData?.list?.length) return;

    this.menuData.list.forEach((item: any) => {
      if (!item?.children?.length) {
        item.expand = false;
        return;
      }

      const matchSelf = this.isRouteActive(item?.railPage) || this.isRouteActive(item?.page);
      const matchChild = item.children.some((child: any) => this.isRouteActive(child?.page));
      item.expand = matchSelf || matchChild;
    });
  }

  //检查是否禁用
  checkDisabled(target: any): boolean {
    if (!this.logined && target.needLogined) return true;
    return false;
  }

  //菜单项是选择列表时的点击选择项动作
  optionsSelect(targetCode: string) {
    if (this.appService.languageCode === targetCode) return;
    this.appService.selectLangFun(targetCode);
    this.leftMenuService.popupStatus = false;
  }

  /**关闭tooltip  */
  closeTooltip(item: any) {
    if (Array.isArray(item)) {
      item.forEach(x => {
        this.closeTooltip(x);
      });
    } else {
      if (item.tooltip) item.tooltip = false;
      if (item.children) this.closeTooltip(item.children);
    }
  }

  /**
   * 敬请期待
   */
  @ViewChild('moveBg') moveBg: any;
  onWait(left: number = 0) {
    this.leftMenuService.popupStatus = false;
    if (left != undefined) this.moveBg.nativeElement.style.left = `${left}%`;
  }

  /**新用户推荐ID输入弹窗 */
  openReferralId() {
    this.popup.open(ReferralIdComponent, {
      inAnimation: this.isH5 ? 'fadeInUp' : undefined,
      outAnimation: this.isH5 ? 'fadeOutDown' : undefined,
      position: this.isH5 ? { bottom: '0px' } : undefined,
      speed: 'faster',
      autoFocus: false,
    });
  }
  /**新用户推荐ID入口是否显示 */
  isShowEnterReferralId() {
    this.allianceApi.getCanBindReferralId().subscribe((res: boolean) => {
      this.friendService.isShowLeftMenuReferraslId$.next(res);
    });
  }

  /**
   * 已有推荐好友的跳转到推荐好友主页，没有的弹窗
   */
  getReferralLinkRoute() {
    this.friendService.getReferralInfo();
    this.friendService.referralInfo$.pipe(untilDestroyed(this)).subscribe(x => {
      this.isShowInviteCodeModel = !x?.totalReferrals;
    });
  }
}
